import {Component, Inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {DeviceId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {ProgressSpinner} from 'primeng/progressspinner';
import {CommonModule} from '@angular/common';
import {DeployableService} from '@webmate-web-core/shared/services/device/deployable.service';
import {DevicePropertyNames, DeviceRequirements, PlatformFamily, getPlatformFamily} from '@webmate-web-core/shared/types/devices/device-properties';
import {
    AdvertisedDeployable, constructRequirementsForDeployableInSlot,
    Deployable,
    DeviceOfferId,
    isAvailable,
    isHeadlessDeployable
} from '@webmate-web-core/shared/types/devices/device-template';
import _ from 'lodash';
import {WebmateBrowserModule} from '@webmate-web-core/shared/components/browser/webmate-browser.module';
import {Browser, browserFromBackendObject, browserSortingFunction} from '@webmate-web-core/shared/types/browser';
import md5 from 'md5';
import {ToggleSwitch, ToggleSwitchChangeEvent} from 'primeng/toggleswitch';
import {Tooltip} from 'primeng/tooltip';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {Observable, Subject, Subscription, TimeoutError, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, switchMap, take} from 'rxjs/operators';
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {ButtonDirective} from 'primeng/button';
import {SplitButton} from 'primeng/splitbutton';
import {MenuItem} from 'primeng/api';
import {DeviceSlotService} from '@webmate-web-core/shared/services/device/device-slot.service';
import {Resolution} from '@webmate-web-core/shared/types/resolution';
import {Checkbox} from 'primeng/checkbox';
import {Select} from 'primeng/select';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {FavoriteDevice, FavoriteDeviceService} from '@webmate-web-core/shared/services/device/favorite-device.service';
import {compareVersionNumbers, makeDataTestingIdForString} from '@webmate-web-core/shared/utils/utils';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {platformComparator} from '@webmate-web-core/shared/types/platform';
import {WorkbenchContextService} from '../../../../workbench/shared/services/workbench-context.service';
import {WorkbenchExpeditionService} from '../../../../workbench/shared/services/workbench-expedition.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {ConsolePlugin} from '../../../../workbench/workbench-plugins/console/console-plugin';
import {WorkbenchLayoutManager} from '../../../../workbench/shared/services/workbench-layout-manager.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faStar, faTv} from '@fortawesome/free-solid-svg-icons';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {WorkbenchOpenService} from '@webmate-web-core/shared/workbench-ipc';

export interface DeployDeviceDialogToolConfigOptions {
    projectId: ProjectId;
    platformFamily: PlatformFamily;
    enablePlatformFamilySwitcher?: boolean;
    selectedGroupingVariant?: 'os' | 'browser'; // defaults to 'os'
    browserSessionDefault?: boolean;
    /**
     * preFilter can be used to filter the deployables before they are grouped. This can be used to implement additional filters that are not covered by the grouping functions, e.g. filtering for mobile vendors.
     */
    preFilter?: (d: Deployable) => boolean;
    /**
     * Predicate to filter browsers shown in the browser grouping columns.
     * Only has an effect when selectedGroupingVariant is 'browser'.
     */
    browserFilter?: (b: Browser) => boolean;
    enableGroupingVariantSwitcher?: boolean;
    includeDeployedDevices?: boolean; // defaults to false
    showAllowAutomation?: boolean;
    allowAutomationDefault?: boolean;
    showStartTestOnDeploy?: boolean;
    startTestOnDeployDefault?: boolean;
    showDeployAndOpen?: boolean;
    /**
     * Optional subtitle shown after the tool name in the header. Long text will be ellipsed.
     */
    toolSubtitle?: string;
    onDeploy?: (selection: DeployDeviceSelection) => void;
}

export class DeployDeviceDialogToolConfig implements WebmateToolConfig {
    toolName = "Deploy Device";
    scrollable = false;

    projectId: ProjectId;
    platformFamily: PlatformFamily;
    enablePlatformFamilySwitcher: boolean;
    selectedGroupingVariant: 'os' | 'browser';
    browserSessionDefault: boolean;
    preFilter?: (d: Deployable) => boolean;
    browserFilter?: (b: Browser) => boolean;
    enableGroupingVariantSwitcher: boolean;
    includeDeployedDevices: boolean;
    showAllowAutomation: boolean;
    allowAutomationDefault: boolean;
    showStartTestOnDeploy: boolean;
    startTestOnDeployDefault: boolean;
    showDeployAndOpen: boolean;
    onDeploy?: (selection: DeployDeviceSelection) => void;

    constructor(options: DeployDeviceDialogToolConfigOptions) {
        if (options.toolSubtitle) {
            this.toolName = options.toolSubtitle;
        }
        this.projectId = options.projectId;
        this.platformFamily = options.platformFamily;
        this.enablePlatformFamilySwitcher = options.enablePlatformFamilySwitcher ?? false;
        this.selectedGroupingVariant = options.selectedGroupingVariant ?? 'os';
        this.browserSessionDefault = options.browserSessionDefault ?? false;
        this.preFilter = options.preFilter;
        this.browserFilter = options.browserFilter;
        this.enableGroupingVariantSwitcher = options.enableGroupingVariantSwitcher ?? false;
        this.includeDeployedDevices = options.includeDeployedDevices ?? false;
        this.showAllowAutomation = options.showAllowAutomation ?? true;
        this.allowAutomationDefault = options.allowAutomationDefault ?? true;
        this.showStartTestOnDeploy = options.showStartTestOnDeploy ?? false;
        this.startTestOnDeployDefault = options.startTestOnDeployDefault ?? false;
        this.showDeployAndOpen = options.showDeployAndOpen ?? false;
        this.onDeploy = options.onDeploy;
    }
}

// Used to group deployables by some property. Each function defines one column in the chooser
// previousSelectedEntryId can be passed and used to apply the previous selected value as an explicit filter.
// This might be necessary in cases where the distribution is overlapping, e.g. the browsers that are available on multiple devices at once
type DeployableGroupingFn = (deployables: Array<DeployableVariants>, previousSelectedEntryId?: EntryId) => DeployableColum;

type EntryId = string;

interface DeployableColumnEntry {
    id: EntryId;
    group: DeployableVariants[];
    representation: EntryRepresentation;
}

interface DeployableDisplayMetadata {
    platform: Device['platform'];
    slotId?: string;
    manufacturerName?: string;
    modelName?: string;
    browsers: Browser[];
    displayName: string;
}

type EntryRepresentation = {
    type: 'label';
    groupLabel: string;
    groupImage?: Observable<string>;
    imageSize?: 'default' | 'large'; // undefined  => default
} | {
    type: 'browsers';
    browsers: Array<Browser>;
}

interface DeployableColum {
    entries: DeployableColumnEntry[];
    displayType: 'column' | 'grid';
    collapsable?: boolean;
};

// Groups together identical Deployables in different variants.
// E.g. the same template in two different slots
// The first entry of the array should be used as a representative of the whole group
type DeployableSource = { kind: 'offer' } | { kind: 'deployed', deviceId: DeviceId, deviceName: string, displayMetadata?: DeployableDisplayMetadata };

interface DeployableVariants {
    id: DeviceOfferId;
    deployables: Deployable[];
    source: DeployableSource;
}

export type DeployDeviceSelection = {
    projectId: ProjectId;
    deployable: Deployable;
    name: string;
    requirements: DeviceRequirements;
    allowAutomation: boolean;
    desiredResolution?: Resolution;
    browserSession: boolean;
    selectedBrowser?: Browser;
    existingDeviceId?: DeviceId;
    startTestOnDeploy: boolean;
    deployAndOpen: boolean;
};

@Component({
    templateUrl: './deploy-device-dialog.component.html',
    styleUrls: ['./deploy-device-dialog.component.less'],
    imports: [
        ProgressSpinner,
        CommonModule,
        WebmateBrowserModule,
        ToggleSwitch,
        Tooltip,
        FormsModule,
        InputText,
        ButtonDirective,
        SplitButton,
        Checkbox,
        Select,
        FaIconComponent
    ],
    standalone: true
})
export class DeployDeviceDialogComponent implements OnInit, OnDestroy {

    // TODO: Idea: Make version column for mobile optional?

    /*
    * The deployables are passed through the filter functions of each entry in the array.
    * Only the filtered values from the previous step are passed to the next one
    * */
    filterDefinitions = {
        'desktop': {
            'os': [
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].platform.platformType)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].platform.platformType, groupImage: this.logoService.getGenericLogoForPlatformType(ds[0].deployables[0].platform.platformType)}, id: ds[0].deployables[0].platform.platformType} as DeployableColumnEntry))}),
                (deployables) => ({
                    displayType: 'column',
                    entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].platform.platformVersion)).map(ds => ({
                        group: ds,
                        representation: {
                            type: 'label',
                            groupLabel: ds[0].deployables[0].platform.platformVersion,
                            groupImage: this.logoService.getLogoForPlatform(ds[0].deployables[0].platform)
                        },
                        id: ds[0].deployables[0].platform.platformVersion
                    } as DeployableColumnEntry)).sort((a, b) => -compareVersionNumbers(this.getConfiguredPlatformVersion(a.group[0].deployables[0].platform.platformVersion), this.getConfiguredPlatformVersion(b.group[0].deployables[0].platform.platformVersion)))
                }),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => ((d.deployables[0].properties[DevicePropertyNames.Locale] as string) || '').toLowerCase())).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].properties[DevicePropertyNames.Locale] || ''}, id: ds[0].deployables[0].properties[DevicePropertyNames.Locale] || ''} as DeployableColumnEntry))}),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].browser)).map(ds => ({group: ds, representation: {type: 'browsers', browsers: ds[0].deployables[0].browser}, id: md5(JSON.stringify(ds[0].deployables[0].browser))} as DeployableColumnEntry))})
            ] as Array<DeployableGroupingFn>,
            'browser': [
                (deployables) => {
                    const tuples = _.flatten(deployables.map(d => d.deployables[0].browser
                        .filter(b => {
                            if (!this.data.browserFilter) return true;
                            const result = this.data.browserFilter(b);
                            return result;
                        })
                        .map(b => ({browser: b.browserType, deployable: d}))));
                    const groupedTuples = _.values(_.groupBy(tuples, (d) => d.browser));
                    return {displayType: 'column', entries: groupedTuples.map(t => ({group: t.map(tt => tt.deployable), representation: {type: 'label', groupLabel: t[0].browser, groupImage: this.logoService.getGenericLogoForBrowserType(t[0].browser)}, id: t[0].browser}))};
                },
                (deployables, previousSelectedEntryId) => {
                    // use previousSelectedEntryId to filter for the browserType selected in the previous column
                    const tuples = _.flatten(deployables.map(d => d.deployables[0].browser.filter(b => b.browserType === previousSelectedEntryId).map(b => ({browser: b.version, deployable: d}))));
                    const groupedTuples = _.values(_.groupBy(tuples, (d) => d.browser));
                    return {displayType: 'column', entries: groupedTuples.map(t => ({group: t.map(tt => tt.deployable), representation: {type: 'label', groupLabel: t[0].browser}, id: t[0].browser})).sort((a, b) => (+b.representation.groupLabel) - (+a.representation.groupLabel))};
                },
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].platform.getFullName())).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].platform.getFullName(), groupImage: this.logoService.getLogoForPlatform(ds[0].deployables[0].platform)}, id: ds[0].deployables[0].platform.getFullName()} as DeployableColumnEntry))}),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => ((d.deployables[0].properties[DevicePropertyNames.Locale] as string) || '').toLowerCase())).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].properties[DevicePropertyNames.Locale] || ''}, id: ds[0].deployables[0].properties[DevicePropertyNames.Locale] || ''} as DeployableColumnEntry))})
            ]  as Array<DeployableGroupingFn>
        },
        'mobile': {
            'os': [
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].platform.platformType)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].platform.platformType, groupImage: this.logoService.getLogoForPlatform(ds[0].deployables[0].platform)}, id: ds[0].deployables[0].platform.platformType} as DeployableColumnEntry))}),
                (deployables) => ({
                    displayType: 'column',
                    entries: _.values(_.groupBy(deployables, (d) => this.getConfiguredPlatformVersion(d.deployables[0].platform.platformVersion))).map(ds => ({
                        group: ds,
                        representation: {type: 'label', groupLabel: this.getConfiguredPlatformVersion(ds[0].deployables[0].platform.platformVersion)},
                        id: this.getConfiguredPlatformVersion(ds[0].deployables[0].platform.platformVersion)
                    } as DeployableColumnEntry)).sort((a, b) => -compareVersionNumbers(this.getConfiguredPlatformVersion(a.group[0].deployables[0].platform.platformVersion), this.getConfiguredPlatformVersion(b.group[0].deployables[0].platform.platformVersion)))
                }),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].properties[DevicePropertyNames.ManufacturerName] as string)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].properties[DevicePropertyNames.ManufacturerName]}, id: ds[0].deployables[0].properties[DevicePropertyNames.ManufacturerName]} as DeployableColumnEntry)), collapsable: true}),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].properties[DevicePropertyNames.ModelName] as string)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].properties[DevicePropertyNames.ModelName], groupImage: this.logoService.getLogoForModel(ds[0].deployables[0].properties[DevicePropertyNames.ManufacturerName], ds[0].deployables[0].properties[DevicePropertyNames.ModelName]), imageSize: 'large'}, id: ds[0].deployables[0].properties[DevicePropertyNames.ModelName]} as DeployableColumnEntry))}),
            ]  as Array<DeployableGroupingFn>,
            'browser': [
                (deployables) => {
                    const tuples = _.flatten(deployables.map(d => d.deployables[0].browser
                        .filter(b => !this.data.browserFilter || this.data.browserFilter(b))
                        .map(b => ({browser: b.browserType, deployable: d}))));
                    const groupedTuples = _.values(_.groupBy(tuples, (d) => d.browser));
                    return {displayType: 'column', entries: groupedTuples.map(t => ({group: t.map(tt => tt.deployable), representation: {type: 'label', groupLabel: t[0].browser, groupImage: this.logoService.getGenericLogoForBrowserType(t[0].browser)}, id: t[0].browser}))};
                },
                (deployables, previousSelectedEntryId) => {
                    const tuples = _.flatten(deployables.map(d => d.deployables[0].browser.filter(b => b.browserType === previousSelectedEntryId).map(b => ({browser: b.version, deployable: d}))));
                    const groupedTuples = _.values(_.groupBy(tuples, (d) => d.browser));
                    return {
                        displayType: 'column',
                        entries: groupedTuples
                            .map(t => ({group: t.map(tt => tt.deployable), representation: {type: 'label', groupLabel: t[0].browser}, id: t[0].browser}))
                            .sort((a, b) => -compareVersionNumbers(a.representation.groupLabel, b.representation.groupLabel))
                    };
                },
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].platform.platformType)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].platform.platformType, groupImage: this.logoService.getLogoForPlatform(ds[0].deployables[0].platform)}, id: ds[0].deployables[0].platform.platformType} as DeployableColumnEntry))}),
                (deployables) => ({
                    displayType: 'column',
                    entries: _.values(_.groupBy(deployables, (d) => this.getConfiguredPlatformVersion(d.deployables[0].platform.platformVersion))).map(ds => ({
                        group: ds,
                        representation: {type: 'label', groupLabel: this.getConfiguredPlatformVersion(ds[0].deployables[0].platform.platformVersion)},
                        id: this.getConfiguredPlatformVersion(ds[0].deployables[0].platform.platformVersion)
                    } as DeployableColumnEntry)).sort((a, b) => -compareVersionNumbers(a.id, b.id))
                }),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].properties[DevicePropertyNames.ManufacturerName] as string)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].properties[DevicePropertyNames.ManufacturerName]}, id: ds[0].deployables[0].properties[DevicePropertyNames.ManufacturerName]} as DeployableColumnEntry)), collapsable: true}),
                (deployables) => ({displayType: 'column', entries: _.values(_.groupBy(deployables, (d) => d.deployables[0].properties[DevicePropertyNames.ModelName] as string)).map(ds => ({group: ds, representation: {type: 'label', groupLabel: ds[0].deployables[0].properties[DevicePropertyNames.ModelName], groupImage: this.logoService.getLogoForModel(ds[0].deployables[0].properties[DevicePropertyNames.ManufacturerName], ds[0].deployables[0].properties[DevicePropertyNames.ModelName]), imageSize: 'large'}, id: ds[0].deployables[0].properties[DevicePropertyNames.ModelName]} as DeployableColumnEntry))}),
            ]  as Array<DeployableGroupingFn>
        }
    };

    selectedGroupingVariant: 'os' | 'browser' = this.data.selectedGroupingVariant;
    activePlatformFamily: PlatformFamily = this.data.platformFamily;

    loading = true;

    groupedDeployables: DeployableVariants[];
    filteredDeployables: DeployableVariants[];

    selectedDeployable: Deployable | undefined;
    private selectedDeployableVariant?: DeployableVariants;
    selectedDeployableDisplayData: AdvertisedDeployable | undefined;
    displayDataSubscription: Subscription | undefined;

    deployableColums: DeployableColum[] = [];

    // For each column stores the selected DeployableColumnEntry by id
    // This is caluclated based on the selectedDeployable
    selectedEntries: EntryId[] = [];

    filterTerm = '';
    filterSubject: Subject<string> = new Subject();
    favoritesOnly = false;
    private favoriteDeviceIds: Set<string> = new Set();
    hasFavorites = false;
    private favoriteDevicesSubscription?: Subscription;

    fullVersion: boolean = true;
    private getConfiguredPlatformVersion(platformVersion: string) {
        return this.fullVersion ? platformVersion : platformVersion.split('.')[0];
    }

    cuttingEdge: boolean = false;

    audioFeatureEnabled = false;
    showAudioFilter = true;
    audioOnly: boolean = false;
    showIosControlsFilter = true;
    iosControls: boolean = false;


    customDeviceName = '';
    allowAutomation: boolean = this.data.allowAutomationDefault;
    shouldStartTestOnDeploy: boolean = false;
    desiredResolution: Resolution = new Resolution(1920, 1080);
    availableResolutions: Array<Resolution> = [
        new Resolution(1280, 800).toDropdownModel(),
        this.desiredResolution.toDropdownModel(),
        new Resolution(2560, 1440).toDropdownModel()
    ];

    requestInProgress = false;
    deployButtonModel: MenuItem[] = [{label: 'Deploy and Open', command: () => this.requestDeploymentOfTemplate(true)}];
    browserSession: boolean = this.data.browserSessionDefault;
    allowStartBrowserSession : boolean = false;

    constructor(private deployableService: DeployableService, private toolService: ModalToolContainerService,
                @Inject(TOOL_CONTAINER_DATA) public data: DeployDeviceDialogToolConfig, private logoService: LogoResolverService,
                private slotService: DeviceSlotService, private deviceService: DeviceService, private favoriteDeviceService: FavoriteDeviceService, private featureService: FeatureService,
                @Optional() private workbenchContextService: WorkbenchContextService,
                @Optional() private workbenchOpenService: WorkbenchOpenService,
                @Optional() private workbenchExpeditionService: WorkbenchExpeditionService, private notificationService: WmNotificationService,
                @Optional() private workbenchLayoutManager: WorkbenchLayoutManager) {}

    ngOnInit() {
        this.shouldStartTestOnDeploy = this.data.startTestOnDeployDefault ?? false;
        this.filterSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map(s => s.toLowerCase())
        ).subscribe(searchString => {
            this.updateDeployableLists();

        });

        this.featureService.getFeature('webmate.audio-streaming').pipe(
            take(1),
            switchMap(f => {
                this.audioFeatureEnabled = f.isActive;
                return this.deployableService.getDeployablesForProject$(this.data.projectId, undefined, true);
            }),
            switchMap(deployables => {
                if (!this.data.includeDeployedDevices) {
                    return of({deployables, devices: [] as Device[]});
                }
                return this.deviceService.getDevicesForProject$(this.data.projectId).pipe(
                    map(devices => ({deployables, devices}))
                );
            })
        ).subscribe(({deployables, devices}) => {
            const preFilteredDeployables = this.data.preFilter ? deployables.filter(d => this.data.preFilter!(d)) : deployables;
            const offerMetadataById = this.buildOfferMetadataMap(preFilteredDeployables);
            const availableDeployables = this.filterAvailableDeployables(preFilteredDeployables);
            const groupedDeployables = Object.entries(_.groupBy(availableDeployables, 'id')).map(value => {
                return {
                    id: value[0],
                    // Shuffle deployables to avoid races for slots
                    deployables: _.shuffle(value[1]),
                    source: { kind: 'offer' } as DeployableSource
                } as DeployableVariants;
            }).sort((a, b) => -platformComparator(a.deployables[0].platform, b.deployables[0].platform));
            const deployedVariants = this.data.includeDeployedDevices ? this.mapDeployedDevices(devices, offerMetadataById) : [];
            this.groupedDeployables = this.prioritizeDeployedDeployables([...deployedVariants, ...groupedDeployables]);
            this.showAudioFilter = this.audioFeatureEnabled && !!availableDeployables.find(d =>
                getPlatformFamily(d.properties) === 'mobile' && d.properties[DevicePropertyNames.AudioStreamingAvailable]
            );
            this.showIosControlsFilter = !!availableDeployables.find(d =>
                getPlatformFamily(d.properties) === 'mobile' && d.properties[DevicePropertyNames.IOSInteractionType] === 'controller'
            );
            this.updateDeployableLists();
            this.loading = false;
        });

        this.subscribeToFavorites();

        // Starting a browser session is available in any workbench mode (default / manual-test / audit),
        // not only audit. It stays disabled outside a workbench context (where there is no active mode).
        this.allowStartBrowserSession = !!this.workbenchContextService?.getCurrentWorkbenchContext();

    }

    ngOnDestroy() {
        this.displayDataSubscription?.unsubscribe();
        this.favoriteDevicesSubscription?.unsubscribe();
    }

    filterChanged() {
        this.updateDeployableLists();
    }

    private setSelectedDeployable(d: Deployable, variant?: DeployableVariants) {
        if (this.selectedDeployable?.id !== d.id) {
            // We have a new/different selected deployable => update the logos
            if (this.displayDataSubscription) {
                this.displayDataSubscription.unsubscribe();
            }
            this.displayDataSubscription = this.logoService.getLogoForDeployable(d).pipe(take(1)).subscribe(a => {
                this.selectedDeployableDisplayData = a;
                this.displayDataSubscription = undefined;
            })
        }
        this.selectedDeployable = d;
        if (variant) {
            this.selectedDeployableVariant = variant;
            if (variant.source.kind === 'deployed') {
                this.customDeviceName = variant.source.deviceName;
            } else {
                this.customDeviceName = '';
            }
        }
    }

    private getFilterStringForDeployable(d: Deployable) {
        let s = d.id;
        // Browser.getFullName already includes the full name of the platform, therefore it's not added explicitly
        s += d.browser.map(b => b.getFullName()).join(' ');
        s += d.properties[DevicePropertyNames.ManufacturerName] || '';
        s += d.properties[DevicePropertyNames.ModelName] || '';
        s += d.properties[DevicePropertyNames.SlotId];
        s += d.properties[DevicePropertyNames.Locale] || '';
        return s.toLowerCase();
    }

    private updateDeployableLists() {
        if (!this.groupedDeployables) {
            return;
        }

        this.filteredDeployables = this.groupedDeployables.filter(v => this.matchesActivePlatformFamily(v.deployables[0]));

        // Apply preFilter if defined
        if (this.data.preFilter) {
            this.filteredDeployables = this.filteredDeployables.filter(v => this.data.preFilter!(v.deployables[0]));
        }

        // Apply filters
        if (this.filterTerm !== '') {
            this.filteredDeployables = this.filteredDeployables.filter(v => this.getFilterStringForDeployable(v.deployables[0]).includes(this.filterTerm.toLowerCase()));
        }

        if (this.favoritesOnly) {
            this.filteredDeployables = this.filteredDeployables.filter(v => this.favoriteDeviceIds.has(v.id));
        }

        // Cutting edge filter
        if (this.cuttingEdge) {
            // TODO refactor without immutable
            const byPlatformType = _.groupBy(this.filteredDeployables, deployable => deployable.deployables[0].platform.platformType);
            this.filteredDeployables = Object.values(byPlatformType).flatMap(deployablesOfSamePlatform => {
                const byVersion = _.groupBy(deployablesOfSamePlatform, deployable => deployable.deployables[0].platform.platformVersion);
                const versions = Object.keys(byVersion);
                if (versions.length === 0) {
                    return [];
                }
                const newestVersion = versions.reduce((latest, version) => {
                    return compareVersionNumbers(version, latest) > 0 ? version : latest;
                }, versions[0]);
                return byVersion[newestVersion] ?? [];
            });
        }

        // Audio Filter
        if (this.showAudioFilter && this.audioOnly) {
            this.filteredDeployables = this.filteredDeployables.filter(d => d.deployables[0].properties[DevicePropertyNames.AudioStreamingAvailable]);
        }

        // iosControlsFilter
        if (this.showIosControlsFilter && this.iosControls) {
            this.filteredDeployables = this.filteredDeployables.filter(d => d.deployables[0].properties[DevicePropertyNames.IOSInteractionType] === 'controller')
        }

        this.filteredDeployables = this.prioritizeDeployedDeployables(this.filteredDeployables);

        //
        // Update or set selected
        if (this.filteredDeployables.length > 0) {
            if (!this.selectedDeployable) {
                this.setSelectedDeployable(this.filteredDeployables[0].deployables[0], this.filteredDeployables[0]);
            } else {
                const selectedDeployable = this.selectedDeployable;
                const previousSelectedVariant = this.filteredDeployables.find(v => v.deployables.some(d => d.id === selectedDeployable.id));
                const previousSelected = previousSelectedVariant?.deployables?.[0];
                if (previousSelected && previousSelectedVariant) {
                    // the previous selected deployable is still availble, update it, in case something changed
                    this.setSelectedDeployable(previousSelected, previousSelectedVariant);
                } else {
                    // previous deployable is not in filtered set, choose a new one
                    this.setSelectedDeployable(this.filteredDeployables[0].deployables[0], this.filteredDeployables[0]);
                }
            }
            // rebuild the columns, preserving selection if columns already exist
            if (this.deployableColums.length === 0) {
                this.buildAllColumns();
            } else {
                this.recalculateColumnsFromIndex(0);
            }
        }
    }

    get currentFilterDefinition(): Array<DeployableGroupingFn> {
        return this.filterDefinitions[this.activePlatformFamily][this.selectedGroupingVariant];
    }

    get canSwitchGroupingVariant(): boolean {
        return !!this.filterDefinitions[this.activePlatformFamily].browser && this.data.enableGroupingVariantSwitcher;
    }

    get selectedDeployableBrowsers(): Browser[] {
        if (!this.selectedDeployable) return [];
        const browsers = this.selectedDeployable.browser;
        const effectiveBrowsers = browsers.length > 0 || this.selectedDeployableVariant?.source.kind !== 'deployed'
            ? browsers
            : (this.selectedDeployableVariant.source.displayMetadata?.browsers ?? []);
        return this.data.browserFilter ? effectiveBrowsers.filter(this.data.browserFilter) : effectiveBrowsers;
    }

    private filterAvailableDeployables(deployables: Deployable[]): Deployable[] {
        // TODO custom sorting function?
        // Headless offers cannot be used for interactive device tests and would break the grouping columns
        // (no platform, model or logo), therefore they are excluded here.
        return deployables.filter(d => isAvailable(d) && !isHeadlessDeployable(d)).sort((a,b) => a.runtimeCost - b.runtimeCost);
    }

    private buildOfferMetadataMap(deployables: Deployable[]): Map<DeviceOfferId, DeployableDisplayMetadata> {
        return new Map(deployables.map(deployable => [deployable.id, {
            platform: deployable.platform,
            slotId: deployable.properties[DevicePropertyNames.SlotId],
            manufacturerName: deployable.properties[DevicePropertyNames.ManufacturerName],
            modelName: deployable.properties[DevicePropertyNames.ModelName],
            browsers: deployable.browser,
            displayName: deployable.modelName
        }]));
    }

    private mapDeployedDevices(devices: Device[], offerMetadataById: Map<DeviceOfferId, DeployableDisplayMetadata>): DeployableVariants[] {
        return devices
            // Headless devices have no interactive console and no meaningful platform / model data
            .filter(device => !device.isHeadlessDevice())
            .map(device => {
                const offerId = device.properties[DevicePropertyNames.DeviceOfferId] as DeviceOfferId | undefined;
                const displayMetadata = offerId ? offerMetadataById.get(offerId) : undefined;
                const properties = {
                    ...device.properties,
                    [DevicePropertyNames.SlotId]: device.properties[DevicePropertyNames.SlotId] || displayMetadata?.slotId,
                    [DevicePropertyNames.ManufacturerName]: device.properties[DevicePropertyNames.ManufacturerName] || displayMetadata?.manufacturerName,
                    [DevicePropertyNames.ModelName]: device.properties[DevicePropertyNames.ModelName] || displayMetadata?.modelName
                };
                const browsersRaw = properties[DevicePropertyNames.MachineBrowsers] as any[] | undefined;
                const browsers = (browsersRaw ?? []).map(browserFromBackendObject).sort(browserSortingFunction);
                const effectiveBrowsers = browsers.length > 0 ? browsers : (displayMetadata?.browsers ?? []);
                const modelName = properties[DevicePropertyNames.ModelName] || displayMetadata?.displayName || device.name;
                const deployableId = properties[DevicePropertyNames.DeviceOfferId] || device.id;
                const platform = this.isKnownPlatform(device.platform) ? device.platform : (displayMetadata?.platform || device.platform);
                const deployable = new Deployable(deployableId, modelName, platform, effectiveBrowsers, properties, 0);
                return {
                    id: device.id,
                    deployables: [deployable],
                    source: { kind: 'deployed', deviceId: device.id, deviceName: device.name, displayMetadata }
                } as DeployableVariants;
            });
    }

    private isKnownPlatform(platform?: Device['platform']): boolean {
        return !!platform && platform.platformType?.toUpperCase() !== 'UNKNOWN';
    }

    private getDisplayPlatform() {
        const selectedDeployable = this.selectedDeployable;
        if (this.isKnownPlatform(selectedDeployable?.platform)) {
            return selectedDeployable?.platform;
        }
        if (this.selectedDeployableVariant?.source.kind === 'deployed') {
            return this.selectedDeployableVariant.source.displayMetadata?.platform;
        }
        return undefined;
    }

    get displayPlatformName(): string | undefined {
        return this.getDisplayPlatform()?.getFullName();
    }

    private getSelectedSlotId(): string | undefined {
        const slotId = this.selectedDeployable?.properties?.[DevicePropertyNames.SlotId];
        if (slotId) {
            return slotId;
        }
        if (this.selectedDeployableVariant?.source.kind === 'deployed') {
            return this.selectedDeployableVariant.source.displayMetadata?.slotId;
        }
        return undefined;
    }

    get displayManufacturerName(): string | undefined {
        return this.selectedDeployable?.properties?.[DevicePropertyNames.ManufacturerName]
            || (this.selectedDeployableVariant?.source.kind === 'deployed' ? this.selectedDeployableVariant.source.displayMetadata?.manufacturerName : undefined);
    }

    get displayModelName(): string | undefined {
        return this.selectedDeployable?.properties?.[DevicePropertyNames.ModelName]
            || (this.selectedDeployableVariant?.source.kind === 'deployed' ? this.selectedDeployableVariant.source.displayMetadata?.modelName : undefined);
    }

    private isDeployedVariant(variant: DeployableVariants): boolean {
        return variant.source.kind === 'deployed';
    }

    private prioritizeDeployedDeployables(list: DeployableVariants[]): DeployableVariants[] {
        const deployed: DeployableVariants[] = [];
        const others: DeployableVariants[] = [];
        list.forEach(v => (this.isDeployedVariant(v) ? deployed : others).push(v));
        return deployed.concat(others);
    }

    private buildAllColumns() {
        if (this.filteredDeployables.length === 0) {
            return;
        }
        this.deployableColums = [];
        this.selectedEntries = [];
        this.recalculateColumnsFromIndex(0);
    }

    recalculateColumnsFromIndex(fromIndex: number) {
        let deployables: DeployableVariants[];
        // Find the super set of deployables from the previous column. For index 0 it's the set of all deployables
        if (fromIndex === 0) {
            deployables = this.filteredDeployables;
        } else {
            // See if the entry of the previous column that has been selected is still availble, otherwise default to the first one
            const previousEntryForSelected = this.selectedEntries[fromIndex - 1];
            const entriesForSelected = this.deployableColums[fromIndex - 1].entries.filter(c => !!this.entryContainsSelected(c));
            const entryForSelected = entriesForSelected.find(e => e.id === previousEntryForSelected) || entriesForSelected[0] || this.deployableColums[fromIndex - 1].entries[0];
            if (!entryForSelected) {
                return;
            }
            deployables = entryForSelected.group;
        }
        const column = this.currentFilterDefinition[fromIndex](deployables, fromIndex > 0 ? this.selectedEntries[fromIndex - 1] : undefined);
        if (this.deployableColums[fromIndex]) {
            // replace entry
            this.deployableColums[fromIndex] = column;
        } else if (this.deployableColums.length === fromIndex) {
            // add new entry
            this.deployableColums.push(column);
        } else {
            throw new Error('Cannot calculate column for index ' + fromIndex);
        }

        if (this.selectedEntries[fromIndex]) {
            const currentSelectedEntry = column.entries.find(entry => entry.id === this.selectedEntries[fromIndex]);
            if (!currentSelectedEntry || !this.entryContainsSelected(currentSelectedEntry)) {
                // The previously selected entry is no longer available for this column. Select a new one
                const newSelectedEntry = column.entries.find(entry => this.entryContainsSelected(entry)) || column.entries[0];
                if (newSelectedEntry) {
                    this.selectedEntries[fromIndex] = newSelectedEntry.id;
                }
            }
        } else {
            const selectedEntryForColumn = column.entries.find(entry => this.entryContainsSelected(entry)) || column.entries[0];
            if (selectedEntryForColumn) {
                this.selectedEntries[fromIndex] = selectedEntryForColumn.id;
            }
        }

        if (fromIndex < this.currentFilterDefinition.length - 1) {
            this.recalculateColumnsFromIndex(fromIndex + 1);
        }
    }

    selectDeployable(entry: DeployableColumnEntry, index: number) {
        const variant = entry.group[0];
        const deployable = variant.deployables[0];
        if (!_.isEqual(deployable, this.selectedDeployable)) {
            this.setSelectedDeployable(deployable, variant);
        }
        this.selectedEntries[index] = entry.id;
        this.recalculateColumnsFromIndex(index);
    }

    entryContainsSelected(entry: DeployableColumnEntry) {
        const selectedDeployable = this.selectedDeployable;
        if (!selectedDeployable) {
            return false;
        }
        return !!entry.group.find(v => v.deployables.some(d => d.id === selectedDeployable.id));
    }

    changeGroupingVariant(event: ToggleSwitchChangeEvent) {
        this.selectedGroupingVariant = event.checked ? 'browser' : 'os';
        this.buildAllColumns();
    }

    switchPlatformFamily(platformFamily: PlatformFamily) {
        if (this.activePlatformFamily === platformFamily) {
            return;
        }
        this.activePlatformFamily = platformFamily;
        this.favoritesOnly = false;
        this.selectedDeployable = undefined;
        this.selectedDeployableVariant = undefined;
        this.selectedDeployableDisplayData = undefined;
        this.selectedEntries = [];
        this.deployableColums = [];
        this.subscribeToFavorites();
        this.updateDeployableLists();
    }

    searchUpdated() {
        this.filterSubject.next(this.filterTerm);
    }

    getColumnEntryTestingId(entry: DeployableColumnEntry, index: number): string {
        return `deploy-device-dialog-column-${index}-entry-${makeDataTestingIdForString(entry.id)}`;
    }

    get slotNameForSelected() {
        const slotId = this.getSelectedSlotId();
        if (slotId) {
            return this.slotService.getSlotInProject$(slotId, this.data.projectId).pipe(map(s => s.name));
        } else {
            return of('');
        }
    }

    get selectedSupportsResolution() {
        return !!this.selectedDeployable?.properties?.[DevicePropertyNames.ChangeResolution];
    }

    get isSelectedDeployableAlreadyDeployed() {
        return this.selectedDeployableVariant?.source.kind === 'deployed';
    }

    requestDeploymentOfTemplate(deployAndOpen: boolean = false) {
        if (!this.selectedDeployable || !this.selectedDeployableDisplayData) {
            return;
        }
        this.requestInProgress = true;
        const selectedDeployable = this.selectedDeployable;
        const name = (this.customDeviceName !== undefined && this.customDeviceName.trim().length > 0) ? this.customDeviceName.trim() : this.selectedDeployableDisplayData.displayName;
        let requirements = constructRequirementsForDeployableInSlot(selectedDeployable, selectedDeployable.properties[DevicePropertyNames.SlotId], this.allowAutomation, this.selectedSupportsResolution ? this.desiredResolution : undefined);
        if (this.data.onDeploy) {
            this.requestInProgress = false;
            this.data.onDeploy({
                projectId: this.data.projectId,
                deployable: selectedDeployable,
                name,
                requirements,
                allowAutomation: this.allowAutomation,
                desiredResolution: this.selectedSupportsResolution ? this.desiredResolution : undefined,
                browserSession: this.browserSession,
                selectedBrowser: this.getSelectedBrowser(),
                existingDeviceId: this.selectedDeployableVariant?.source.kind === 'deployed'
                    ? this.selectedDeployableVariant.source.deviceId
                    : undefined,
                startTestOnDeploy: this.shouldStartTestOnDeploy,
                deployAndOpen
            });
        } else {
            this.deviceService.requestDeployment$(this.data.projectId, requirements, name).subscribe(
                next => {

                    if (this.workbenchLayoutManager) {
                        this.workbenchLayoutManager.instantiateAndShowViewTemplate(ConsolePlugin.ViewTemplateId, {
                            id: next.id,
                            name: next.name
                        });
                    } else if (deployAndOpen) {
                        this.workbenchOpenService.openConsole(this.data.projectId, next.id).pipe(take(1)).subscribe();
                    } else {
                        // TODO handle case without workbench.
                    }

                    this.requestInProgress = false;
                    this.customDeviceName = '';
                    this.toolService.closeLastTool();
                    if (this.browserSession) {
                        this.startBrowserSession(next.id);
                    }
                },
                err => {
                    const msg = "Request for deployment failed, error was: " + ((typeof(err['error']) === 'string' ? err['error'] : undefined) || 'unknown');
                    console.error(msg, err);
                    this.requestInProgress = false;
                }
            );
        }
    }

    /**
     * Start a browser session on the freshly deployed device with the selected browser, make it the
     * active session and open its console. Only possible inside a workbench context (where the
     * expedition service is available); a no-op otherwise.
     */
    startBrowserSession(deviceId: DeviceId): void {
        const selectedBrowser = this.getSelectedBrowser();
        if (!selectedBrowser || !this.workbenchExpeditionService) {
            return;
        }

        this.workbenchExpeditionService.startSessionOnActiveConsole$(deviceId, selectedBrowser).subscribe({
            error: (error) => {
                if (error instanceof TimeoutError) {
                    this.notificationService.toastErrorNotification('Error launching browser: timeout');
                } else {
                    this.notificationService.toastErrorNotification('Error launching browser: ' + (error.message || 'unknown'));
                }
                console.error('error launching browser', error);
            }
        });
    }

    private getSelectedBrowser(): Browser | undefined {
        if (this.selectedGroupingVariant !== 'browser' || !this.selectedDeployable) {
            return undefined;
        }
        const [browserType, version] = this.selectedEntries;
        return this.selectedDeployable.browser.find(b =>
            b.browserType === browserType &&
            b.version === version
        );
    }

    getDeployButtonText(): string {
        if (this.requestInProgress) {
            return "Deploying";
        } else {
            return "Deploy";
        }
    }

    isFavorite(selectedDeployable: Deployable): boolean {
        return this.favoriteDeviceService.isFavoriteDevice(this.activePlatformFamily, this.data.projectId, selectedDeployable.id);
    }

    toggleFavorite(selectedDeployable: Deployable) {
        const favorite: FavoriteDevice = {
            id: selectedDeployable.id,
            platform: selectedDeployable.platform,
            platformType: selectedDeployable.platform.platformType,
            manufacturerName: selectedDeployable.properties[DevicePropertyNames.ManufacturerName],
            modelName: selectedDeployable.modelName
        };

        this.favoriteDeviceService.storeFavoriteDevice(this.activePlatformFamily, this.data.projectId, favorite);
    }

    private subscribeToFavorites() {
        this.favoriteDevicesSubscription?.unsubscribe();
        this.favoriteDevicesSubscription = this.favoriteDeviceService.getFavoriteDevices$(this.activePlatformFamily, this.data.projectId).subscribe(favorites => {
            this.favoriteDeviceIds = new Set(favorites.map(fav => fav.id));
            this.hasFavorites = this.favoriteDeviceIds.size > 0;
            if (!this.hasFavorites && this.favoritesOnly) {
                this.favoritesOnly = false;
            }
            this.updateDeployableLists();
        });
    }

    private matchesActivePlatformFamily(deployable: Deployable): boolean {
        return getPlatformFamily(deployable.properties) === this.activePlatformFamily;
    }

    isDeployedEntry(entry: DeployableColumnEntry): boolean {
        return entry.group.some(variant => this.isDeployedVariant(variant));
    }

    protected readonly DevicePropertyNames = DevicePropertyNames;
    protected readonly faStar = faStar;
    protected readonly faTv = faTv;
}
